/**
 * Russian dictionary — also the source of the `Dictionary` type (see
 * ./types.ts): add a key here first, then to en.ts and zh.ts.
 */
const ru = {
  appName: "LK Storekeeper",
  login: {
    subtitle: "Войдите под учётной записью администратора или кладовщика.",
    username: "Имя пользователя",
    password: "Пароль",
    signIn: "Войти",
    signingIn: "Выполняется вход...",
    loginFailed: "Ошибка входа",
  },
  header: {
    logout: "Выйти",
    language: "Язык",
    theme: "Тема",
    themeLight: "Светлая",
    themeDark: "Тёмная",
    themeSystem: "Системная",
    session: "Сессия",
    sessionAccess: "Токен доступа",
    sessionRefresh: "Вход действителен",
    sessionExpired: "истёк",
    networkDebug: "Отладка сети",
    otherStatuses: "Другие статусы",
    shortReceiptNumbers: "Номер ПЭ без /N",
    allPages: "Все страницы",
  },
  online: {
    label: "Кто в сети",
    online: "В сети",
    offline: "Не в сети",
    /** Online and the app tab is visible and focused. */
    active: "Активен",
    /** Online, but the app tab is in the background / unfocused. */
    idle: "Вкладка неактивна",
    empty: "Сейчас никого нет в сети",
    you: "вы",
    never: "ещё не заходил(а)",
    loading: "Загрузка…",
  },
  tabs: {
    ui: "Интерфейс",
    db: "База данных",
  },
  tablesIndex: {
    heading: "Таблицы",
    editable: "Редактируемо",
    columns: (count: number) => `${count} столбцов`,
    notFound: "Таблица не найдена в базе данных",
  },
  tableView: {
    addRow: "Добавить строку",
    edit: "Изменить",
    noRows: "Строк пока нет.",
    pageInfo: (page: number, totalPages: number, total: number) => `Страница ${page} из ${totalPages} (${total} строк)`,
    previous: "Назад",
    next: "Далее",
    addTitle: "Добавить строку",
    editTitle: "Изменить строку",
    cancel: "Отмена",
    save: "Сохранить",
    saving: "Сохранение...",
    loading: "Загрузка...",
    /** Placeholder for the empty option in a foreign-key <select>. */
    selectPlaceholder: "— выбрать —",
    /** Shown while options for a foreign-key <select> are being fetched. */
    loadingOptions: "Загрузка вариантов...",
    /** Shown when options for a foreign-key <select> could not be loaded. */
    optionsLoadFailed: "Не удалось загрузить варианты",
    /** Placeholder for the local-search box inside a searchable dropdown. */
    searchPlaceholder: "Поиск...",
    /** Shown when a search inside a searchable dropdown matches nothing. */
    noSearchResults: "Ничего не найдено",
    /** Placeholder for the server-side table search box. */
    searchTable: "Поиск...",
  },
  errors: {
    loadFailed: "Не удалось загрузить таблицу",
    saveFailed: "Не удалось сохранить",
    deleteFailed: "Не удалось удалить",
    /** Backend crashed (HTTP 5xx) — shown instead of the raw server text. */
    serverError: "Ошибка на сервере. Попробуйте ещё раз; если повторится — сообщите администратору.",
    /** The proxy could not reach the backend (HTTP 502). */
    backendUnreachable: "Сервер склада недоступен. Проверьте соединение и попробуйте позже.",
    /** Database constraint violations, by the backend's `code`. */
    duplicate: "Такая запись уже существует.",
    stillReferenced: "Запись используется в других данных, поэтому её нельзя удалить или изменить.",
    missingReference: "Связанная запись не найдена — возможно, её удалили.",
    requiredField: "Не заполнено обязательное поле.",
    integrityViolation: "Изменение противоречит уже сохранённым данным.",
  },
  /** Generic labels for the reusable confirmation dialog. */
  /** Measurement units, keyed by the canonical unit `Measure` is given. */
  units: {
    kg: "кг",
    m3: "м³",
    kgM3: "кг/м³",
  },
  confirm: {
    yes: "Да",
    no: "Нет",
    confirm: "Подтвердить",
    cancel: "Отмена",
    close: "Закрыть",
    /** "Discard changes?" dialog shown when a form modal is closed unsubmitted. */
    discardTitle: "Отменить изменения?",
    discardMessage:
      "Форма не отправлена. Если закрыть её сейчас, введённые данные будут потеряны.",
    discardConfirm: "Отменить",
    keepEditing: "Продолжить заполнение",
  },
  workflow: {
    /** Freight receipt (ПЭ) picker shown at the top of every step. */
    receiptPicker: {
      heading: "Поручение экспедитору",
      placeholder: "— выберите поручение экспедитору —",
      newButton: "Новое ПЭ",
      newTitle: "Новое поручение экспедитору",
      orderNumber: "Номер заказа",
      cargoName: "Наименование груза",
      /** Склад of the ПЭ. */
      warehouse: "Склад",
      isOurFirstMile: "Наша первая миля",
      plannedWeightKg: "Плановый вес (кг)",
      plannedVolumeM3: "Плановый объём (м³)",
      /** Tooltip on a weight/volume/SF chip sourced from the supplier's plan. */
      plannedFigure: "Плановое значение (от отправителя)",
      /** Tooltip on a weight/volume/SF chip showing the current remainder
       *  (received minus shipped/written off), from the stock ledger. */
      actualFigure: "Текущий остаток (приёмки минус отгрузки/списания)",
      /** Hint under the order number field explaining the length limit. */
      orderNumberHint: (max: number) => `Не более ${max} символов.`,
      create: "Создать",
      creating: "Создание...",
      noReceipts: "Экспедиторских расписок пока нет.",
      /** Opens the "edit this receipt" dialog. Available to every role. */
      editButton: "Редактировать ПЭ",
      editTitle: "Редактирование поручения экспедитору",
      sequenceNumber: "Номер ПЭ в заказе",
      renumberWarning: (from: string, to: string) =>
        `Номер ${from} будет заменён на ${to} везде, включая коды типоразмеров и историю операций. Ранее напечатанные этикетки перестанут совпадать.`,
      save: "Сохранить",
      saving: "Сохранение...",
      saved: "Изменения сохранены.",
      /** Cargo name as supplied, plus its per-locale translations. */
      cargoNameOriginal: "Наименование (как в оригинале)",
      cargoNameRu: "Наименование (рус.)",
      cargoNameEn: "Наименование (англ.)",
      cargoNameZh: "Наименование (кит.)",
      /** Section heading for the translation fields in the edit dialog. */
      cargoNameTranslations: "Переводы наименования",
      totalPackageCount: "Всего мест",
      cargoReadyOn: "Дата готовности груза",
      selectedLabel: (receiptNumber: string) => `ПЭ ${receiptNumber}`,
      /** Icon-button tooltip/label for opening the QR scanner. */
      scanButton: "Сканировать QR-код заказа",
      scanTitle: "Сканировать QR-код заказа",
      scanHint: "Наведите камеру на QR-код заказа.",
      /** Shown over the video while the camera stream is still starting. */
      scanLoading: "Запуск камеры...",
      /** Shown when camera access fails or is denied. */
      scanCameraError: "Не удалось получить доступ к камере. Проверьте разрешения и попробуйте снова.",
      /** Title for the picker shown when a scanned order number matches more than one receipt. */
      scanMatchesTitle: "Выберите поручение",
      /** Shown when a scanned order number matches no receipt. */
      scanNotFound: (orderNumber: string) => `Поручение экспедитору для заказа ${orderNumber} не найдено.`,
      /** Icon-button tooltip/label for opening the "share receipt" modal. */
      shareButton: "Поделиться поручением",
      shareTitle: "Поделиться поручением",
      /** Copies receipt number, order number, sequence number, etc. as text. */
      shareCopyReceipt: "Скопировать номер поручения",
      /** Copies the deep-link URL to this receipt. */
      shareCopyLink: "Скопировать ссылку",
      /** Temporary confirmation shown on a copy button after a successful copy. */
      shareCopied: "Скопировано!",
      shareQrHint: "Отсканируйте, чтобы открыть это поручение.",
      /** Supplier section in the create dialog. */
      supplier: "Отправитель",
      /** Placeholder for the supplier dropdown when none is picked. */
      supplierPlaceholder: "— выберите отправителя —",
      /** Search box placeholder inside the supplier dropdown. */
      supplierSearchPlaceholder: "Поиск отправителя...",
      /** Shown when the supplier search matches nothing. */
      supplierNoResults: "Отправители не найдены.",
      /** Option label for clearing the supplier selection. */
      supplierNone: "Без отправителя",
      supplierAddAnother: "Добавить отправителя",
      supplierRemove: "Убрать отправителя",
      /** Tracking number(s) for the shipment, stored on the expected arrival. */
      trackingNumbers: "Трек-номер(а)",
      /** Expected arrival date at the warehouse, stored on the expected arrival. */
      warehouseArrivalOn: "Дата прибытия",
      /** Label for the user who created this receipt. */
      createdBy: "Создал",
      /** Label for the status field in the edit dialog. */
      statusLabel: "Статус",
      /** Label for the free-text comment field (Комментарий ПЭ). */
      commentLabel: "Комментарий",
      commentPlaceholder: "Добавьте комментарий к поручению…",
      /** Per-status localized labels, keyed by the Latin enum code. */
      statusNames: {
        awaiting_arrival: "Ожидает поступления",
        partially_received: "Поступил частично",
        received: "Поступил",
        scheduled_for_trip: "Запланирован в рейс",
        partially_shipped: "Частично убыл",
        shipped: "Отгружен",
      },
    },
    /** Secondary in-page steps navigation. */
    steps: {
      /** Primary tab: ПЭ fields + package-types table + action buttons. */
      main: "Основная",
      /** Primary tab: unified log of every warehouse operation. */
      operations: "Складские операции",
      packageTypes: "Типоразмеры",
      receiving: "Приёмка",
      shipping: "Отгрузка/Списание",
      transfer: "Перемещение",
      balances: "Остатки",
      connections: "Связи",
      files: "Файлы",
      chat: "Чат",
      /** Label of the overflow menu holding the less-used steps. */
      more: "Ещё",
      /** «ожид. пост» — every expected arrival of the receipt. */
      arrivals: "Ожид. пост.",
      /** «доп услуги» — additional services (placeholder). */
      services: "Доп. услуги",
    },
    /** основная (Main) tab: ПЭ fields + the package-types table. */
    main: {
      /** Heading over the ПЭ's own fields. */
      receiptFields: "Данные ПЭ",
      cargoName: "Наименование груза",
      plannedWeightKg: "Плановый вес (кг)",
      plannedVolumeM3: "Плановый объём (м³)",
      actualVolumeM3: "Фактический объём (м³)",
      totalPackageCount: "Всего мест",
      /** Heading over the package-types table. */
      packageTypesHeading: "Типоразмеры",
      colDimensions: "Типоразмер",
      colCode: "Код упаковки",
      colPackaging: "Вид упаковки",
      colCount: "Мест",
      colWeight: "Вес",
      colLocation: "Локация",
      /** Receiving dates per package type, each with the count received that day. */
      colReceivedOn: "Дата приёмки",
      /** Tooltip/label on the per-row "print box sticker" button. */
      printSticker: "Печать стикера коробки",
      /** Button opening the receiving form modal. */
      openReceiving: "Приёмка",
      /** Button opening the shipping form modal. */
      openShipping: "Списание",
      /** Shown when the receipt has no package types yet. */
      noPackageTypes: "Для этого ПЭ пока нет типоразмеров.",
      /** Expand/collapse the per-location breakdown of a row. */
      toggleBreakdown: "Показать по локациям",
      /** Heading over the warehouse block (actions + package types). */
      warehouseHeading: "Склад",
      warehouseDescription: "Приёмка, отгрузка и типы упаковки этого ПЭ.",
      colVolume: "Объём",
    },
    /** The receipt page header (number, status, plan vs accepted, dates). */
    receiptHeader: {
      date: "Создано",
      manager: "Менеджер",
      /** Shown when the receipt has no manager. */
      managerPlaceholder: "Не назначен",
      planHeading: "Плановые характеристики груза",
      acceptedHeading: "На складе",
      packages: "Мест",
      weight: "Вес",
      volume: "Объём",
      density: "SF",
      cargoReadyOn: "Дата готовности груза",
      receivedOn: "Получено",
      comment: "Комментарий",
      /** In-place comment editor on the receipt page. */
      commentPlaceholder: "Добавьте комментарий к поручению…",
      saveComment: "Сохранить",
      savingComment: "Сохранение…",
      savedComment: "Сохранено",
    },
    /** «ожид. пост» tab: every expected arrival of the receipt. */
    arrivals: {
      heading: "Ожидаемые поступления",
      /** Small tag over the heading ("наша первая миля"). */
      tag: "Наша первая миля",
      empty: "По этому ПЭ нет ожидаемых поступлений.",
      /** Column: the receipt is on «наша первая миля». */
      ourFirstMile: "Наша первая миля",
      supplier: "Отправитель",
      addressAndContacts: "Адрес и контакты",
      contacts: "Контактные данные",
      trackingNumbers: "Трек-номер",
      plannedArrival: "Дата прибытия план",
      /** This supplier's share of the receipt plan. */
      plannedPackages: "Мест план",
      plannedWeight: "Вес план",
      plannedVolume: "Объём план",
      arrivedOn: "Дата прибытия факт",
      deleteSupplier: "Удалить отправителя",
      deleteTitle: "Удалить отправителя?",
      /** Confirmation body; the supplier name, or null for a row without one. */
      deleteBody: (supplier: string | null) =>
        supplier
          ? `Отправитель «${supplier}» будет убран из этого ПЭ.`
          : "Строка ожидаемого поступления без отправителя будет удалена.",
      deleteWarning: "Вместе с ним удалятся трек-номер и план прибытия этой строки.",
    },
    /** «доп услуги» tab — placeholder until services are modelled. */
    services: {
      heading: "Доп. услуги",
      placeholder: "Учёт дополнительных услуг появится позже.",
      palletizing: "Палетирование",
      marking: "Маркировка",
      soon: "Скоро",
    },
    /** складские операции (Warehouse operations) tab. */
    operationsLog: {
      /** Operation totals: places, weight, volume. */
      colPackages: "Мест",
      colWeight: "Вес",
      colVolume: "Объём",
      heading: "Складские операции",
      description: "Все операции по этому ПЭ.",
      empty: "Операций по этому ПЭ пока нет.",
      colType: "Тип документа",
      colNumber: "Номер",
      colDate: "Дата",
      colComment: "Комментарий",
      typeReceiving: "Приёмка",
      typeShipping: "Отгрузка",
      typeTransfer: "Перемещение",
      typeReceiptTransfer: "Перенос",
      /** e.g. "Приёмка №1 от 01.09.2026" */
      detailTitle: (type: string, id: string, date: string) => `${type} ${id} от ${date}`,
      detailColPackageType: "Типоразмер",
      detailColCount: "Мест",
      detailColWeight: "Вес",
      detailColLocation: "Локация",
      /** A receiving's expeditor's / warehouse receipt number (<ПЭ>/<приёмка>). */
      receiptDocument: (number: string) => `Расписка ${number}`,
      /** The shipping order a departure executed. */
      shippingOrder: (number: string) => `Наряд ${number}`,
    },
    /** Client-side box sticker (стикер коробки) print dialog. */
    boxSticker: {
      title: "Стикеры коробок",
      /** e.g. "2 из 10" */
      printOne: "Печать",
      printAll: "Печать всех",
      /** Shown when the package type has no places in stock to label. */
      empty: "Нет мест для маркировки: сначала проведите приёмку.",
      labelReceipt: "ПЭ",
      labelOrder: "Заказ",
      labelCode: "Код",
      labelDimensions: "Д*Ш*В",
      labelPlace: "Место",
      /** Sticker layout toggle. */
      orientation: "Ориентация",
      vertical: "Вертикальный",
      horizontal: "Горизонтальный",
      /** Background PDF rendering (`/labels/box-jobs`). */
      generating: "Формируем стикеры…",
      /** A single place's print button while its sticker renders. */
      generatingOne: "Формируем…",
      progress: (done: number, total: number) => `${done} из ${total}`,
      ready: "Стикеры готовы",
      openPdf: "Открыть PDF",
      failed: "Не удалось сформировать стикеры",
    },
    /** The receipts overview shown before a receipt is picked. */
    receiptOverview: {
      /** Pager footer: how many of the matching receipts are on screen. */
      shownOf: (shown: number, total: number) => `Показано ${shown} из ${total}`,
      loadMore: "Показать ещё",
      heading: "Ожидаемые поступления",
      loading: "Загрузка ожидаемых поступлений...",
      empty: "Нет ожидаемых поступлений на складе.",
      receiptNumber: "№ ПЭ",
      cargo: "Груз",
      supplier: "Отправитель",
      supplierAddress: "Адрес",
      supplierPhone: "Телефон",
      supplierEmail: "Эл. почта",
      cargoReadyOn: "Готовность",
      /** ДатаПолученияГрузаНаСкладе: the date of the first receiving. */
      receivedOn: "Получено",
      arrivalOn: "Прибытие",
      trackingNumbers: "Трек-номер",
      /** Contact(s) for tracking the shipment, from the expected arrival. */
      trackingContacts: "Контакты для отслеживания",
      /** Title of the dialog opened from the (clamped) tracking-contacts cell. */
      contactsDialogTitle: (receipt: string) => `Контакты ПЭ ${receipt}`,
      copy: "Копировать",
      copied: "Скопировано",
      /**
       * Short forms of the planned-figure labels, for the overview cards.
       * The value already carries the unit, so these drop it.
       */
      packagesShort: "Мест",
      weightShort: "Вес",
      volumeShort: "Объём",
      sfShort: "SF",
      /** Tooltip on a translated cargo name, showing the original. */
      originalName: (name: string) => `В оригинале: ${name}`,
      /** Label for the user who created this receipt. */
      createdBy: "Создал",
      /**
       * Column headers for the planned figures, which the register keeps
       * separate from the actual stock on hand (see `inWarehouse`).
       */
      plannedPackages: "Мест план",
      plannedWeight: "Вес план",
      plannedVolume: "Объём план",
      /**
       * A shipped receipt shows what actually departed instead of the plan
       * (departures only — write-offs and the split-off remainder excluded).
       */
      shippedPackages: "Мест отгружено",
      shippedWeight: "Вес отгружено",
      shippedVolume: "Объём отгружено",
      /**
       * «Другие статусы» mixes planned and shipped rows, so its headers drop
       * "план" and each figure carries one of these captions instead.
       */
      planCaption: "план",
      shippedCaption: "отгружено",
      /** Date the supplier dispatched the cargo. */
      supplierShippedOn: "Дата отправки",
      /** Planned arrival date at the warehouse. */
      plannedArrivalOn: "План поступления",
      /** Planned-arrival date filter on the «Ожидаем» tab. */
      arrivalFrom: "План поступления с",
      arrivalTo: "по",
      datePlaceholder: "дд.мм.гггг",
      overdueOnly: "Только просроченные",
      resetFilters: "Сбросить",
      /** Shown when the date filter matches nothing. */
      filteredEmpty: "Нет ПЭ с плановой датой поступления в выбранном периоде.",
      /** Badge on a receipt whose planned arrival date has passed. */
      overdue: "Просрочено",
      /** Free-text note from the expected-arrival register. */
      comment: "Комментарий",
    },
    /**
     * The landing screen's two tabs: what is coming, and what is here. Kept
     * deliberately short — they sit side by side on a 360px phone, where the
     * full register names ("Ожидаемые поступления") wrapped onto two lines.
     */
    landing: {
      /** Banner above the tabs while shipping orders are open; links to `/shipping`. */
      tripBanner: (count: number) => {
        const n = count % 100;
        const word =
          n % 10 === 1 && n !== 11 ? "наряд" : n % 10 >= 2 && n % 10 <= 4 && (n < 12 || n > 14) ? "наряда" : "нарядов";
        return `${count} ${word} к отгрузке`;
      },
      tripBannerAction: "Выбрать наряды рейса и отгрузить",
      expectedTab: "Ожидаем",
      inWarehouseTab: "На складе",
      otherTab: "Другие статусы",
    },
    /**
     * "Accepted into the warehouse" — receipts with stock still on hand.
     * Every figure is aggregated by the backend (GET /stock/in-warehouse);
     * these are only its labels.
     */
    inWarehouse: {
      heading: "Принято на склад",
      loading: "Загрузка складского остатка...",
      empty: "На складе нет груза с остатком.",
      receiptNumber: "№ ПЭ",
      cargo: "Наименование груза",
      packages: "Мест",
      weight: "Вес",
      volume: "Объём",
      /** Stowage factor / density = weight / volume, kg/m³. */
      sf: "SF",
      acceptedOn: "Дата приёмки",
      status: "Статус груза",
      /** Days elapsed since acceptance (дней на СВХ). */
      daysInWarehouse: "Дней на СВХ",
      comment: "Комментарий",
      /** Days count rendered with its unit, e.g. "12 дн.". */
      days: (count: number) => `${count} дн.`,
    },
    packageTypes: {
      heading: "Типоразмеры",
      code: "Код",
      dimensionsLabel: "Габариты (Д*Ш*В)",
      lengthCm: "Длина (см)",
      widthCm: "Ширина (см)",
      heightCm: "Высота (см)",
      add: "Добавить типоразмер",
      adding: "Добавление...",
      addToggle: "Добавить типоразмер",
      cancel: "Отмена",
      empty: "Для этого ПЭ пока нет типоразмеров.",
      selectReceiptFirst: "Сначала выберите поручение экспедитору выше.",
      delete: "Удалить",
      deleting: "Удаление...",
      deleteConfirmTitle: "Удаление типоразмера",
      deleteConfirmMessage: (code: string) => `Удалить типоразмер ${code}?`,
      deleteConfirmWarning: "Действие нельзя отменить.",
    },
    /** Cards shown in place of the workflow steps when no freight receipt is selected yet. */
    expectedArrivals: {
      heading: "Ожидаемые поступления",
      loading: "Загрузка ожидаемых поступлений...",
      empty: "Сначала выберите поручение экспедитору выше.",
      orderNumber: "Заказ",
      cargoType: "Тип груза",
      itemCount: "Количество единиц",
      packageCount: "Количество мест",
      plannedWeightKg: "Плановый вес (кг)",
      plannedVolumeM3: "Плановый объём (м³)",
      /** Stowage factor / cargo density = weight / volume, in kg/m³. Computed, not stored. */
      sf: "SF (кг/м³)",
      trackingNumbers: "Номера отслеживания",
      trackingContacts: "Контакты для отслеживания",
      supplierShippedOn: "Дата отправки",
      arrivalOn: "Ожидаемое прибытие",
      cargoReadyOn: "Дата готовности груза",
      status: "Статус",
      notes: "Примечания",
    },
    /** Who posted an operation, shown in the recent-operations lists. */
    operationUser: "Пользователь",
    /** Placeholder for an operation with no recorded user. */
    operationUserUnknown: "Не указан",
    /** The condition catalogs: «Условия обработки» of a ПЭ and
     *  «Фактическое состояние груза при приеме» of a receiving. */
    conditions: {
      /** aria-label of the «?» button that opens a condition's description. */
      help: "Описание",
      emptyCatalog: "Справочник пуст.",
      handlingTitle: "Условия обработки",
      handlingAdd: "Указать условия обработки",
      /** aria-label of the conditions row, which opens the editor. */
      handlingEdit: "Изменить условия обработки",
      cargoTitle: "Фактическое состояние груза при приеме",
      cargoHint: "Можно выбрать несколько. Описание — по наведению или по значку «?».",
      cargoEdit: "Изменить",
      cargoNone: "Не указано",
    },
    lines: {
      heading: "Строки",
      packageType: "Типоразмер",
      packagingType: "Вид упаковки",
      noPackagingTypes: "Справочник видов упаковки пуст.",
      location: "Локация",
      fromLocation: "Локация откуда",
      toLocation: "Локация куда",
      packageCount: "Количество мест",
      actualWeightKg: "Фактический вес (кг)",
      addLine: "Добавить строку",
      removeLine: "Удалить",
      /** Confirmation prompt shown before removing a line that has data. */
      removeLineConfirm: "Удалить эту строку? Введённые данные будут потеряны.",
      noPackageTypes: "Сначала добавьте типоразмер.",
      /** Select option / placeholder that keeps a line on a brand-new size. */
      newPackageTypeOption: "➕ Новый типоразмер",
      noLocations: "Нет доступных складских локаций.",
      /** Validation error shown when a line is missing its package type. */
      missingPackageType: "Выберите типоразмер для каждой строки.",
      missingPackagingType: "Выберите вид упаковки для каждой строки.",
      /** Validation error shown when a line is missing its warehouse location. */
      missingLocation: "Выберите складскую локацию для каждой строки.",
      /** Validation error shown when a line is missing its actual weight. */
      missingWeight: "Укажите фактический вес для каждой строки.",
      /** «Вместимость (шт)»: pieces one place holds; asked only on lines of a
       *  combining packaging (pallets), required there. */
      capacityPcs: "Вместимость (шт)",
      /** Under a receiving line in the operations log. */
      capacityValue: (pcs: number) => `Вместимость: ${pcs} шт`,
      missingCapacity: "Укажите вместимость (шт) — целое число не меньше 1 — для каждой строки с паллетой.",
      /** Placeholder for the (now optional) location select. */
      locationUnspecified: "Не указана",
    },
    receiving: {
      heading: "Приёмка",
      description: "Фиксация фактически принятых мест груза по строкам.",
      /** Mode switch: "assistant" also creates package types, "receiving" only posts the document. */
      modeAssistant: "Помощник создания приёмки",
      modeReceiving: "Приёмка",
      /** Per-mode description shown under the mode switch. */
      modeAssistantHint:
        "Создаёт типоразмеры и проводит приёмку: укажите размеры новых мест прямо здесь.",
      modeReceivingHint:
        "Только проводит приёмку по уже созданным типоразмерам.",
      /** Shown in "receiving" mode when the receipt has no package types to receive against. */
      noPackageTypesForReceiving:
        "У этого ПЭ ещё нет типоразмеров. Сначала создайте их через «Помощник создания приёмки».",
      operationDate: "Дата и время приёмки",
      /** Optional free-text note stored on the operation. */
      comment: "Комментарий",
      commentPlaceholder: "Необязательный комментарий к приёмке…",
      /** Inline package-type creation, so sizes can be entered while receiving. */
      newPackageType: "Новый типоразмер",
      newPackageTypeHint: "Укажите Д×Ш×В — типоразмер будет добавлен в это ПЭ.",
      addPackageType: "Добавить типоразмер",
      addingPackageType: "Добавление...",
      /** Photo capture attached to the receipt (also listed under Documents). */
      photos: "Фотографии приёмки",
      photosHint: "Фотографии прикрепляются к ПЭ и видны на вкладке «Документы».",
      /** Structured photo-requirement checklist rendered as a numbered list. */
      photoGuideTitle: "Требования к фото",
      photoGuideItems: [
        {
          term: "Общий вид партии",
          detail: "всё на паллете/поддоне, показать объём и укладку.",
        },
        {
          term: "Каждое место",
          detail: "2–3 ракурса, видна целостность упаковки.",
        },
        {
          term: "Маркировка",
          detail: "крупным планом, текст читаем без увеличения.",
        },
        {
          term: "Повреждения",
          detail: "общий, средний, крупный план (с линейкой).",
        },
        {
          term: "Укладка и крепление",
          detail: "вид спереди/сбоку/сверху, плёнка, ремни, прокладки.",
        },
        {
          term: "Документы",
          detail: "реквизиты читаемы; фото документа рядом с грузом.",
        },
      ],
      photoGuideNote: "Важно: хорошее освещение, прямой угол.",
      addPhotos: "Сделать фото",
      uploadingPhotos: "Загрузка...",
      photoCount: (count: number) => `Фотографий: ${count}`,
      removePhoto: "Удалить фото",
      /** Shown to non-warehouse roles: receiving is read-only for them. */
      readOnly: "Приёмку груза нельзя редактировать.",
      submit: "Провести приёмку",
      submitting: "Проведение...",
      success: "Операция приёмки проведена.",
      recent: "Последние операции приёмки",
      empty: "Операций приёмки пока нет.",
      /** Header for the totals column in the recent operations table. */
      total: "Итого",
      /** Title of the confirm dialog shown before posting the operation. */
      confirmTitle: "Подтверждение приёмки",
      /** Body of the confirm dialog shown before posting the operation. */
      confirmMessage: "Провести операцию приёмки?",
      /** Arrival-result prompt after a receiving that doesn't meet the plan. */
      arrivalResultQuestion: "Принятый груз не совпадает с планом. Какой статус установить ПЭ?",
      arrivalResultArrived: "Поступил",
      arrivalResultPartial: "Поступил частично",
      /** Shown instead of the prompt when the plan is met in full. */
      arrivalResultAuto: "Груз соответствует плану — ПЭ получит статус «Поступил».",
    },
    shipping: {
      heading: "Отгрузка/Списание",
      description: "Фиксация отгруженных или списанных мест груза.",
      operationDate: "Дата и время отгрузки",
      comment: "Комментарий",
      /** Optional free-text note stored on the operation. */
      commentPlaceholder: "Необязательный комментарий к отгрузке…",
      reason: "Вид отгрузки",
      reasonDeparture: "Убытие",
      reasonWriteOff: "Списание",
      submit: "Провести отгрузку",
      submitting: "Проведение...",
      success: "Операция отгрузки проведена.",
      recent: "Последние операции отгрузки",
      empty: "Операций отгрузки пока нет.",
      /** Header for the totals column in the recent operations table. */
      total: "Итого",
      /** Title of the confirm dialog shown before posting the operation. */
      confirmTitle: "Подтверждение отгрузки",
      /** Body of the confirm dialog shown before posting the operation. */
      confirmMessage: "Провести операцию отгрузки?",
      /** Write-off: nothing of this receipt is on hand, so there is nothing to write off. */
      noStock: "По ПЭ нет груза на складе — списывать нечего.",
      /** A line picks a package type / location pair with no stock. */
      notInStock: "В строке выбран типоразмер и ячейка, где нет остатка.",
      /** Lines ask for more than is on hand in one package type / location. */
      exceedsPlaces: (code: string, available: string) => `${code}: мест больше, чем в остатке (доступно ${available}).`,
      exceedsWeight: (code: string, available: string) => `${code}: вес больше, чем в остатке (доступно ${available} кг).`,
      /** Hint under a line: what is still available in that bucket. */
      available: (places: string, weight: string) => `Доступно: ${places} мест, ${weight} кг`,
      /** Heading of the departure form opened by «Отгрузить» on a shipping order. */
      departureHeading: (orderNumber: string) => `Убытие по наряду ${orderNumber}`,
      writeOffHeading: "Списание",
      departureDescription:
        "Строки подобраны по остаткам в локациях. Проверьте их: наряд исполняется только целиком.",
      writeOffDescription:
        "Списание мест груза (брак, утрата). Убытие оформляется только по наряду на отгрузку.",
      loadingDraft: "Подбираем строки по остаткам…",
      /** Shortages of the stock against the order: it can't be shipped. */
      shortagesTitle: "Остатка не хватает — отгрузить наряд нельзя:",
      shortage: (code: string, requestedPlaces: string, availablePlaces: string, requestedKg: string, availableKg: string) =>
        `${code}: нужно ${requestedPlaces} мест / ${requestedKg} кг, доступно ${availablePlaces} мест / ${availableKg} кг`,
      /** The form's lines don't add up to an order line: it executes only whole. */
      notWhole: (code: string, places: string, kg: string, gotPlaces: string, gotKg: string) =>
        `Наряд исполняется только целиком: ${code} — нужно ${places} мест / ${kg} кг, в форме ${gotPlaces} мест / ${gotKg} кг.`,
      submitDeparture: "Провести убытие",
      submitWriteOff: "Провести списание",
      confirmDeparture: "Провести убытие? После этого наряд будет выполнен и изменить его будет нельзя.",
      confirmWriteOff: "Провести списание?",
      /** Write-off mode: the confirm dialog title and the posted message. */
      confirmTitleWriteOff: "Подтверждение списания",
      successWriteOff: "Списание проведено.",
    },
    /** Наряды на отгрузку of the ПЭ, on the main tab. */
    shippingOrders: {
      /** Link to `/shipping`, every open order of every ПЭ. */
      allOrders: "Все наряды — отгрузка рейса",
      heading: "Наряды на отгрузку",
      description: "Создаются в 1С при формировании рейса. Груз отгружается только по наряду и только целиком.",
      empty: "Нарядов на отгрузку нет.",
      ship: "Отгрузить",
      /** Chip of an executed order. */
      executed: (date: string) => `Выполнен ${date}`,
      /** Chip of an order waiting for its departure. */
      pending: "К отгрузке",
      createdBy: "Создал",
      /** Toggle that shows the current stock of a line's package type by location. */
      showStock: "Остатки по локациям",
      noStock: "Остатка нет",
      /** Temporary: creating an order here until 1C does it. */
      create: "Создать наряд",
      createTitle: "Новый наряд на отгрузку (временно)",
      createHint: "Временная кнопка: наряды должны приходить из 1С. Наряд создаётся в пределах свободного остатка.",
      weightKg: "Вес (кг)",
      free: (places: string, weight: string) => `Свободно: ${places} мест, ${weight} кг`,
      noFreeStock: "Свободного остатка нет: весь груз уже в нарядах или не принят.",
    },
    /** Deletion marks of documents and catalog items. */
    deletionMark: {
      /** Chip on a document marked for deletion (it is then not posted). */
      marked: "Помечен на удаление",
      filterLabel: "Пометка удаления",
      filterAll: "Все",
      filterUnmarked: "Без пометки",
      filterMarked: "Помеченные на удаление",
      mark: "Пометить на удаление",
      unmark: "Снять пометку удаления",
      markConfirmTitle: "Пометка удаления",
      markConfirmMessage: (number: string) =>
        `Пометить ПЭ ${number} на удаление? Оно станет непроведённым и останется в списках с пометкой. Пометить можно только ПЭ без складских операций.`,
      unmarkConfirmMessage: (number: string) => `Снять пометку удаления с ПЭ ${number}? Оно снова будет проведено.`,
      /** A marked ПЭ takes no new documents. */
      readOnly: "ПЭ помечено на удаление: новые операции по нему недоступны.",
    },
    transfer: {
      heading: "Перемещение",
      description: "Перемещение мест груза между складскими локациями.",
      occurredAt: "Дата и время",
      submit: "Провести перемещение",
      submitting: "Проведение...",
      success: "Операция перемещения проведена.",
      recent: "Последние операции перемещения",
      empty: "Операций перемещения пока нет.",
      /** Header for the totals column in the recent operations table. */
      total: "Итого",
      /** Title of the confirm dialog shown before posting the operation. */
      confirmTitle: "Подтверждение перемещения",
      /** Body of the confirm dialog shown before posting the operation. */
      confirmMessage: "Провести операцию перемещения?",
    },
    balances: {
      heading: "Складские остатки",
      description: "Рассчитано по регистру складских остатков для выбранного ПЭ.",
      refresh: "Обновить",
      location: "Локация",
      packageType: "Типоразмер",
      balance: "Остаток",
      /** Column header for the weight-on-hand balance (kg). */
      weightBalance: "Вес (кг)",
      empty: "По этому ПЭ пока нет складских движений.",
      /** SF (density) computed on the form: weight / volume, kg/m³. */
      sfHeading: "SF",
      sfDescription:
        "Рассчитывается на форме: фактический вес (из приёмки) ÷ объём (габариты типоразмера × доступный остаток), кг/м³. Не редактируется.",
      sfWeight: "Вес, кг",
      sfVolume: "Объём, м³",
      sf: "SF, кг/м³",
      sfTotal: "Итого",
      sfEmpty: "Для расчёта нужны фактический вес и габариты типоразмера.",
    },
    /** Read-only diagram of receiving/transfer/shipping flows for the selected receipt. */
    connections: {
      heading: "Связи",
      description: "Движение мест груза по ПЭ: приёмка в локации, перемещения между локациями, отгрузка/списание.",
      legendReceived: "Принято",
      legendTransferred: "Перемещено",
      legendShipped: "Отгружено / списано",
      legendReceiptTransfer: "Перенос",
      /** Heading for the list of receipt transfer operations. */
      receiptTransferOperations: "Операции переноса между ПЭ (архив разделения)",
      /** Heading for the lines within a receipt transfer operation. */
      receiptTransferOperationLines: "Строки операций переноса между ПЭ (архив разделения)",
      sinkDeparture: "Убыло",
      sinkWriteOff: "Списано",
      empty: "Добавьте типоразмеры к этому ПЭ, чтобы увидеть связи.",
      /** Chronological list of every individual receiving/shipping/transfer line. */
      breakdown: {
        heading: "Полная детализация всех движений",
        date: "Дата",
        type: "Тип",
        packageType: "Типоразмер",
        from: "Откуда",
        to: "Куда",
        count: "Количество",
        typeIn: "Приёмка",
        typeOut: "Отгрузка/Списание",
        typeTransfer: "Перемещение",
        receiptTransferOutPrefix: "Перенос в ПЭ",
        receiptTransferInPrefix: "Перенос из ПЭ",
        empty: "По этому ПЭ пока не проведено ни одного движения.",
      },
    },
    /** Drag-and-drop overlay shared by the chat and the receipt files panel. */
    fileDrop: {
      hint: "Отпустите, чтобы прикрепить файлы",
      unsupported: (names: string) => `Этот формат не поддерживается: ${names}`,
    },
    documents: {
      heading: "Файлы",
      description: "Фотографии и файлы, прикреплённые к этому поручению экспедитору.",
      formats: "Поддерживаемые форматы: JPG, PNG, WEBP, HEIC, PDF, DOC, DOCX, XLS, XLSX.",
      addPhotos: "Добавить фото",
      addFiles: "Добавить файлы",
      uploading: "Загрузка...",
      empty: "Файлов пока нет.",
      selectReceiptFirst: "Сначала выберите поручение экспедитору выше.",
      download: "Открыть",
      delete: "Удалить",
      deleteConfirmTitle: "Удалить документ",
      deleteConfirmMessage: "Удалить этот документ? Действие необратимо.",
      storageDisabled: "Хранилище файлов не настроено на сервере.",
      tooLarge: "Файл слишком большой.",
      /** Label for the upload date shown in the per-file submenu. */
      uploadedOn: "Загружен",
      /** Shown over a thumbnail when the image fails to load. */
      imageLoadError: "Не удалось загрузить фото",
      saveFile: "Скачать",
      previewError: "Не удалось открыть таблицу. Скачайте файл, чтобы посмотреть его.",
      previewEmpty: "Лист пуст.",
      previewTruncated: (shown: number, total: number) => `Показаны первые ${shown} из ${total} строк.`,
    },
    chat: {
      heading: "Чат",
      description: "Обсуждение этого поручения экспедитору.",
      /** Title inside the docked chat box; the receipt number follows. */
      receiptHeading: "Чат поручения ",
      selectReceiptFirst: "Сначала выберите поручение экспедитору выше.",
      empty: "Сообщений пока нет. Начните обсуждение.",
      loading: "Загрузка сообщений...",
      placeholder: "Написать сообщение...",
      send: "Отправить",
      sending: "Отправка...",
      attachPhoto: "Прикрепить фото",
      attachFile: "Прикрепить файл",
      systemBadge: "Система",
      you: "Вы",
      unknownSender: "Неизвестный отправитель",
      imageLoadError: "Не удалось загрузить фото",
      messageActions: "Действия с сообщением",
      copyMessage: "Копировать сообщение",
      replyTo: "Ответить на сообщение",
      replyingTo: "Ответ на",
      cancelReply: "Отменить ответ",
      deleteMessage: "Удалить сообщение",
      deleteConfirm: "Удалить это сообщение? Действие необратимо.",
      messageDeleted: "Сообщение удалено",
      /** Divider label marking where this browser's unread messages start. */
      unreadDivider: "Непрочитанные сообщения",
      attachmentsPending: (count: number) => `Файлов к отправке: ${count}`,
      /** Tooltip/label on the "add emoji reaction" button. */
      addReaction: "Добавить реакцию",
      /** Heading above the people who have read a message (message menu). */
      readBy: "Прочитали",
      /** Shown instead of readers once a message is older than receipts are kept. */
      readReceiptsExpired: "Отметки о прочтении хранятся 3 дня",
      /** Labels for the ✓ / ✓✓ ticks on your own messages. */
      messageSent: "Отправлено",
      messageRead: "Прочитано",
      /** Tooltip/label on the composer's sticker-tray button. */
      stickers: "Стикеры",
      /** Placeholder while the sticker packs are being fetched. */
      stickersLoading: "Загрузка стикеров...",
      /** Button at the top of a thread that fetches earlier messages. */
      loadOlder: "Загрузить предыдущие сообщения",
    },
    /** App-wide chat launcher + conversation list (available on every page). */
    mainChat: {
      title: "Все чаты",
      openLabel: "Открыть чаты",
      back: "Все чаты",
      loading: "Загрузка чатов...",
      channelMain: "Общий чат",
      channelDevelopers: "Чат с разработчиками",
      channelOnecSync: "Ошибки обмена с 1С",
      openSyncFailed: "Ждут повтора",
      sectionChannels: "Каналы",
      sectionReceipts: "Поручения экспедитору",
      noMessagesYet: "Сообщений пока нет",
      /** Label on the header button that opens the receipt's workflow. */
      openReceipt: "Перейти в ПЭ",
      /** Accessible label on a conversation's unread-count badge. */
      unreadCount: (count: number) => `Непрочитанных: ${count}`,
      /** Browser tab title while there are unread messages, e.g. "(3) Messages". */
      documentTitle: (count: number | string) => `(${count}) Сообщения`,
      /** Realtime connection indicator (title/aria-label on the status dot). */
      live: "На связи",
      connecting: "Подключение…",
      offline: "Нет связи",
      /** Extends the feed further back to reach older conversations. */
      loadMore: "Загрузить более старые чаты",
    },
    backendUnavailable: "Складской backend недоступен. Убедитесь, что он запущен.",
  },
  pages: {
    back: "← Назад",
    title: "Все страницы",
    subtitle: "Служебные страницы приложения.",
    uiDescription: "Приёмка, отгрузка и перемещения по ПЭ.",
    dbDescription: "Прямое редактирование таблиц базы данных.",
    archDescription: "Все процессы и бизнес-правила в виде диаграмм.",
    syncFailedDescription: "Объекты, которые 1С не приняла несколько раз и которые ждут ручного повтора.",
    credentialsDescription: "Пароль и сообщение для нового пользователя.",
    syncQueueDescription: "Что ждёт отправки в 1С при следующем обмене.",
    syncLogDescription: "Журнал обмена с 1С: что и когда пришло и ушло.",
    filesDescription: "Все файлы в хранилище S3 и из 1С: поиск, фильтры, сортировка.",
    shippingDescription: "Открытые наряды на отгрузку всех ПЭ: выбрать наряды рейса и отгрузить одной кнопкой.",
  },
  /** `/shipping`: open shipping orders of every ПЭ, shipped as one trip. */
  tripShipping: {
    title: "Отгрузка рейса",
    subtitle: "Открытые наряды на отгрузку всех ПЭ. Отметьте наряды рейса и отгрузите их одной кнопкой: если хоть по одному наряду не хватает груза, не отгрузится ничего.",
    back: "← Все страницы",
    refresh: "Обновить",
    search: "Номер наряда, ПЭ или груз",
    empty: "Нарядов к отгрузке нет.",
    noMatch: "Ничего не найдено.",
    /** Group checkbox: every order dated that day. */
    selectDay: (date: string) => `Выбрать все наряды за ${date}`,
    places: "мест",
    ordersCount: (count: number) => `нарядов: ${count}`,
    selectAll: "Выбрать все",
    /** Label before the ПЭ number on a card. */
    receipt: "ПЭ",
    orderLabel: "наряд",
    noDimensions: "Размеры не указаны",
    /** Where the cargo of a line lies now (locations with stock). */
    where: "Лежит",
    noStock: "На складе нет",
    viewByReceipt: "По ПЭ",
    viewByLocation: "По локациям",
    /** Above the loading list: what it covers. */
    scopeSelected: (count: number) => `Лист по выбранным нарядам: ${count}.`,
    scopeAll: (count: number) => `Все открытые наряды: ${count}. Отметьте наряды рейса во вкладке «По ПЭ», чтобы лист был только по ним.`,
    listTitle: "Лист погрузки",
    copyText: "Копировать",
    copied: "Скопировано",
    print: "Печать",
    volumeUnknown: "Объём не посчитан: не у всех типоразмеров указаны размеры.",
    /** «1 место», «3 места», «12 мест». */
    placesN: (count: number) => {
      const n = count % 100;
      const word = n % 10 === 1 && n !== 11 ? "место" : n % 10 >= 2 && n % 10 <= 4 && (n < 12 || n > 14) ? "места" : "мест";
      return `${count} ${word}`;
    },
    receiptMarked: "ПЭ помечено на удаление",
    readOnly: "Отгружать могут только кладовщики и администраторы.",
    /** Sticky bar under the list. */
    selected: (count: number) => `Выбрано нарядов: ${count}`,
    clear: "Снять выбор",
    ship: "Отгрузить рейс",
    confirmTitle: "Отгрузить рейс?",
    confirmText: (count: number) => `Будут проведены убытия по ${count} нарядам с текущей датой. Сначала проверяются остатки: если хоть по одному наряду не хватает груза, не отгрузится ничего.`,
    confirm: "Отгрузить",
    checking: "Проверяем остатки…",
    progress: (done: number, total: number) => `Отгружено ${done} из ${total}…`,
    blockedTitle: "Рейс не отгружен",
    blockedText: "Ничего не отгружено. Не хватает груза:",
    /** One package type short of what the orders ask for. */
    shortage: (code: string, needPlaces: string, needKg: string, havePlaces: string, haveKg: string) =>
      `${code}: нужно ${needPlaces} мест (${needKg} кг), на складе ${havePlaces} мест (${haveKg} кг)`,
    /** Several orders of one ПЭ that only fail together. */
    together: "вместе",
    doneTitle: "Рейс отгружен",
    doneText: (count: number) => `Проведено убытий: ${count}.`,
    stoppedTitle: "Отгрузка остановлена",
    stoppedText: (order: string) => `Наряд ${order} не отгружен, отгрузка остановлена:`,
    shipped: "Уже отгружены",
    left: "Не отгружены",
    close: "Закрыть",
  },
  files: {
    title: "Файлы",
    subtitle: "Фото из чатов, документы ПЭ и файлы из 1С. Поиск, фильтры и сортировка выполняются на сервере.",
    search: "Поиск по названию или номеру ПЭ",
    source: "Источник",
    sourceAll: "Все источники",
    sourceStorage: "Хранилище S3",
    sourceOnec: "1С",
    kind: "Тип",
    kindAll: "Все типы",
    kinds: { image: "Изображения", pdf: "PDF", spreadsheet: "Таблицы", document: "Документы", other: "Другое" },
    from: "С",
    to: "По",
    sort: "Сортировка",
    sorts: {
      "created_at:desc": "Сначала новые",
      "created_at:asc": "Сначала старые",
      "name:asc": "Название А→Я",
      "name:desc": "Название Я→А",
      "size:desc": "Сначала большие",
      "size:asc": "Сначала маленькие",
    },
    view: "Вид",
    viewGrid: "Сетка",
    viewTable: "Таблица",
    reset: "Сбросить",
    name: "Название",
    receipt: "ПЭ",
    size: "Размер",
    created: "Добавлен",
    open: "Открыть",
    empty: "Файлов не найдено.",
    shown: (n: number, total: number) => `Показано ${n} из ${total}`,
    /** Recorded size of every file matching the filters (not just those loaded). */
    totalSize: (size: string) => `Общий объём: ${size}`,
    loadMore: "Показать ещё",
    refresh: "Обновить",
    back: "← Все страницы",
  },
  credentials: {
    title: "Генератор паролей",
    subtitle: "Генерирует пароль, текст сообщения для пользователя и bcrypt-хеш для users.password_hash. Всё считается в браузере.",
    login: "Логин",
    host: "Адрес",
    password: "Пароль",
    regenerate: "Сгенерировать",
    message: "Сообщение",
    copyMessage: "Скопировать сообщение",
    hash: "bcrypt-хеш",
    hashing: "Считается…",
    hashHint: "Вставьте хеш в password_hash при создании пользователя.",
    copy: "Копировать",
    copied: "Скопировано",
  },
  sync: {
    back: "← Все страницы",
    failedTitle: "1С: ждут повтора",
    failedSubtitle: "После трёх неудачных попыток объект больше не отдаётся 1С автоматически. Разберитесь с ошибкой и нажмите «Повторить».",
    queueTitle: "1С: очередь обмена",
    queueSubtitle: "Что 1С заберёт при следующем обмене, в том порядке, в котором заберёт. Объект уходит из очереди, когда 1С подтвердит его ревизию.",
    emptyQueue: "Очередь пуста — 1С всё забрала.",
    position: "№",
    revision: "Ревизия",
    logTitle: "1С: журнал обмена",
    logSubtitle: "Новые записи сверху. Хранится 90 дней.",
    objectType: "Тип",
    objectGuid: "GUID",
    errors: "Ошибок",
    error: "Ошибка",
    enqueuedAt: "В очереди с",
    retry: "Повторить",
    retried: "Отправлен в очередь заново",
    empty: "Ничего не ждёт повтора.",
    emptyLog: "Записей нет.",
    time: "Время",
    direction: "Направление",
    action: "Действие",
    result: "Результат",
    ok: "Успех",
    failed: "Ошибка",
    inbound: "1С → ЛК",
    outbound: "ЛК → 1С",
    all: "Все",
    searchGuid: "Поиск по GUID",
    loadMore: "Показать ещё",
    loading: "Загрузка…",
    refresh: "Обновить",
    actions: { create: "Создание", update: "Обновление", ack: "Подтверждение", fail: "Отказ 1С" } as Record<string, string>,
    types: {
      forwarding_orders: "ПЭ",
      expected_arrivals: "Ожидаемое поступление",
      counterparties: "Контрагент",
      warehouses: "Склад",
      files: "Файл",
    } as Record<string, string>,
    ackRemoved: (rev: number) => `Удалён из очереди (ревизия ${rev}).`,
    ackStale: (sent: number, current: number) =>
      `Не удалён: ревизия не совпадает — 1С подтвердила ${sent}, в очереди ${current}. Нужно перечитать объект и подтвердить ревизию ${current}, которую отдаёт очередь.`,
    ackNotQueued: (rev: number) =>
      `Не удалён: объекта нет в очереди (ревизия ${rev}) — подтверждён раньше, не ставился в очередь или неверный тип / GUID.`,
    failCounted: (rev: number) => `1С не смогла записать объект (ревизия ${rev}); ошибка засчитана.`,
    failNotCounted: (rev: number) =>
      `Ошибка по ревизии ${rev} не засчитана: объект изменился после чтения или его нет в очереди.`,
    fieldsSent: "Поля от 1С",
    sentGuid: "Присланный GUID",
    shown: (shown: number, total: number) => `Показано ${shown} из ${total}`,
  },
  arch: {
    title: "Архитектура",
    subtitle: "Как устроен склад и приложение — простыми словами, для тех, кто видит проект впервые. Начните со схемы «Как работает склад», незнакомые слова ищите в словаре. Схемы на русском.",
    renderError: "Не удалось отрисовать диаграмму. Исходный код:",
    /** Pan / zoom toolbar of every diagram. */
    viewer: {
      zoomIn: "Приблизить",
      zoomOut: "Отдалить",
      fit: "Вписать целиком",
      fullscreen: "На весь экран",
      close: "Закрыть",
      hint: "Перетаскивание — сдвиг, Ctrl + колесо или два пальца — масштаб, двойной щелчок — приблизить",
      hintFullscreen: "Колесо или два пальца — масштаб, перетаскивание — сдвиг, Esc — закрыть",
    },
    /** «How to read the diagrams» box: one line per Mermaid shape. */
    howToRead: {
      title: "Как читать схемы",
      items: [
        "Прямоугольник — шаг или правило. Стрелки показывают, что за чем идёт.",
        "Ромб — вопрос или развилка; ответы подписаны на выходящих стрелках.",
        "Цилиндр — место, где хранятся данные.",
        "Пунктир — пояснение или связь, а не следующий шаг. Прямоугольник без стрелок — отдельное правило.",
        "Большую схему можно приблизить или открыть на весь экран кнопками над ней.",
      ],
    },
    /** Glossary card; the terms themselves are Russian, in src/lib/archGlossary.ts. */
    glossary: {
      title: "Словарь",
      description: "Термины, которые встречаются на схемах.",
    },
    /** The two parts of the page, `ARCH_GROUPS` in src/lib/archDiagrams.ts. */
    groups: {
      business: { title: "Как работает склад", description: "Бизнес-процессы: что происходит с грузом и по каким правилам. Читайте по порядку." },
      technical: { title: "Как устроено приложение", description: "Для разработчиков и администраторов: из каких частей состоит система и как они общаются." },
    },
    sections: {
      overview: { title: "Коротко: путь груза", description: "Весь процесс на одной схеме — от заказа до отгрузки. Начните отсюда." },
      lifecycle: { title: "Статусы ПЭ", description: "В каком состоянии бывает партия груза и что переводит её из одного в другое." },
      receiving: { title: "Приёмка", description: "Что происходит, когда груз приехал, и как система решает, приехало всё или только часть." },
      operations: { title: "Отгрузка, списание, перемещение", description: "Как груз уходит со склада или меняет ячейку и что система проверяет перед этим." },
      shipping: { title: "Наряды и убытие", description: "Как 1С формирует рейс из нарядов на отгрузку и как кладовщик отгружает наряд целиком." },
      ledger: { title: "Как считается остаток", description: "Остаток не хранится числом — он считается по журналу приходов и расходов. Операции нельзя удалить." },
      fact: { title: "Факт груза", description: "Сколько груза на самом деле: пока груз на складе — то, что лежит; после отгрузки — то, что уехало." },
      masterdata: { title: "ПЭ, типоразмеры и справочники", description: "Как выдаются номера, что нельзя удалить и откуда берётся план." },
      ui: { title: "Экран /ui", description: "Что видит сотрудник: стартовые вкладки, карточка ПЭ и его вкладки." },
      reports: { title: "Списки и отчёты", description: "Откуда берутся цифры на вкладках «На складе», «Ожидаем», в карточке ПЭ и в поиске." },
      files: { title: "Файлы", description: "Фото и документы: загруженные в приложении и присланные из 1С." },
      chat: { title: "Чат", description: "Чат у каждой ПЭ, общие каналы и правила сообщений." },
      dataflow: { title: "Путь данных", description: "Откуда приходят данные, как проверяются, где хранятся и кто их видит. Каждый поток — своя цветная полоса, читается слева направо; флажок «→ ③» значит «продолжение в потоке ③», там оно отмечено «из ②»." },
      system: { title: "Карта системы", description: "Из каких частей состоит приложение и кто с кем общается." },
      auth: { title: "Вход и права", description: "Как происходит вход, как долго он действует и что разрешено каждой роли." },
      onec: { title: "Обмен с 1С", description: "Как приложение и 1С обмениваются данными: сначала 1С забирает изменения, потом присылает свои." },
      onecqueue: { title: "Очередь для 1С", description: "Что происходит с изменённой ПЭ, пока 1С её не забрала, и что делать при ошибках." },
      realtime: { title: "Мгновенные сообщения", description: "Как чат и список «кто онлайн» обновляются без перезагрузки страницы." },
    },
  },
};

export default ru;
